/**
 * Stripe's UI language.
 *
 * Deliberately its own module, free of store / network imports — like
 * [`./session-invoice`](./session-invoice.ts) — so the mapping stays a pure, unit-testable
 * function. `./stripe` itself pulls in the Redux store for its auth header, which would drag the
 * whole app into any test that touched this.
 */

/**
 * The Stripe locale for one of THIS app's language codes.
 *
 * Stripe Elements renders its own copy — field labels, validation errors, the card-brand hints —
 * and defaults to `'auto'`, which reads the BROWSER's language. That is not our language: a buyer
 * whose browser is in English gets an English card form inside a Spanish checkout. So every
 * `<Elements>` must be told explicitly.
 *
 * The mapping is not a pass-through, for two reasons:
 *
 * - This app's Spanish is keyed **`mx`** (see `src/locales/`), which is neither a BCP-47 tag nor
 *   a Stripe locale.
 * - Stripe has no `es-MX`. Its Latin American Spanish is **`es-419`**, which is the correct
 *   target for Mexico — plain `es` is European Spanish.
 *
 * Anything unrecognised falls back to `'auto'` rather than being forwarded: an unsupported value
 * would be worse than letting Stripe guess from the browser, which is at least a real locale.
 */
export type StripeLocale = 'auto' | 'en' | 'es' | 'es-419';

const STRIPE_LOCALE_BY_APP_LANGUAGE: Record<string, StripeLocale> = {
  mx: 'es-419',
  es: 'es',
  en: 'en',
};

export function stripeLocale(appLanguage: string | undefined | null): StripeLocale {
  if (!appLanguage) return 'auto';
  // i18next hands back region-tagged values too (`en-US`), so match the base subtag as well.
  const lower = appLanguage.toLowerCase();
  return STRIPE_LOCALE_BY_APP_LANGUAGE[lower] ?? STRIPE_LOCALE_BY_APP_LANGUAGE[lower.split('-')[0]] ?? 'auto';
}
